import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpenCheck,
  Calculator,
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { logModuloEvento } from '../../utils/bitacora';
import { useTableSort, sortRows, SortableTh } from '../../utils/tableSort';
import {
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

interface FilaCosto {
  empresa_id: number;
  lote_num: string;
  ciclo_cosecha: string;
  tipo_costo: 'insumos' | 'mano_obra' | 'maquinaria' | 'cif';
  periodo: string;
  monto_crc: number;
}

interface CifPeriodo {
  id: string;
  periodo_desde: string;
  periodo_hasta: string;
  monto_total_crc: number;
  descripcion: string;
  rubro_nombre: string | null;
  estado: 'pendiente' | 'distribuido' | 'anulado';
  creado_at: string;
}

type SortKey = 'periodo' | 'lote_num' | 'ciclo_cosecha' | 'tipo_costo' | 'monto_crc';

const TIPO_LABEL: Record<FilaCosto['tipo_costo'], string> = {
  insumos: 'Insumos',
  mano_obra: 'Mano de obra',
  maquinaria: 'Maquinaria',
  cif: 'CIF',
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtCRC(v?: number | null) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return 'CRC ' + n.toLocaleString('es-CR', { maximumFractionDigits: 0 });
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(`${s}T00:00:00`).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function Kpi({ label, value, icon, tone }: { label: string; value: string | number; icon: React.ReactNode; tone: string }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

export function CosteoProduccionABPage() {
  const empresaId = useEmpresaId();
  const permisos = useAppPermisos();
  const [fechaImplementacion, setFechaImplementacion] = useState<string | null>(null);
  const [fechaImplInput, setFechaImplInput] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const [fechaDesde, setFechaDesde] = useState(addDays(today(), -30));
  const [fechaHasta, setFechaHasta] = useState(today());
  const [q, setQ] = useState('');
  const [filas, setFilas] = useState<FilaCosto[]>([]);
  const [cifPeriodos, setCifPeriodos] = useState<CifPeriodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState<'mo' | 'maq' | null>(null);
  const [distribuyendo, setDistribuyendo] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const [showCif, setShowCif] = useState(false);
  const [cifDesde, setCifDesde] = useState(fechaDesde);
  const [cifHasta, setCifHasta] = useState(fechaHasta);
  const [cifMonto, setCifMonto] = useState('');
  const [cifRubro, setCifRubro] = useState('');
  const [cifDescripcion, setCifDescripcion] = useState('');
  const [savingCif, setSavingCif] = useState(false);

  const sort = useTableSort<SortKey>({ key: 'monto_crc', direction: 'desc' });

  const cargarConfig = useCallback(async () => {
    const { data } = await supabase
      .from('app_ab_produccion_config')
      .select('fecha_implementacion')
      .eq('empresa_id', empresaId)
      .maybeSingle();
    setFechaImplementacion(data?.fecha_implementacion ?? null);
    setFechaImplInput(data?.fecha_implementacion ?? '');
  }, [empresaId]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    const [{ data: filasData, error: eFilas }, { data: cifData, error: eCif }] = await Promise.all([
      supabase
        .from('v_app_ab_costo_produccion_lote')
        .select('empresa_id, lote_num, ciclo_cosecha, tipo_costo, periodo, monto_crc')
        .eq('empresa_id', empresaId)
        .gte('periodo', fechaDesde.slice(0, 7) + '-01')
        .lte('periodo', fechaHasta),
      supabase
        .from('app_ab_cif_periodos')
        .select('id, periodo_desde, periodo_hasta, monto_total_crc, descripcion, rubro_nombre, estado, creado_at')
        .eq('empresa_id', empresaId)
        .order('periodo_desde', { ascending: false })
        .limit(50),
    ]);
    if (eFilas) setError(eFilas.message);
    else if (eCif) setError(eCif.message);
    setFilas((filasData ?? []) as FilaCosto[]);
    setCifPeriodos((cifData ?? []) as CifPeriodo[]);
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargarConfig(); }, [cargarConfig]);
  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    const rows = filas.filter((f) => {
      if (!term) return true;
      return [f.lote_num, f.ciclo_cosecha, TIPO_LABEL[f.tipo_costo]].some((x) => String(x ?? '').toLowerCase().includes(term));
    });
    return sortRows(rows, sort.value, (row, key) => row[key]);
  }, [filas, q, sort.value]);

  const totales = useMemo(() => {
    const porTipo: Record<string, number> = { insumos: 0, mano_obra: 0, maquinaria: 0, cif: 0 };
    let total = 0;
    for (const f of filtradas) {
      porTipo[f.tipo_costo] = (porTipo[f.tipo_costo] ?? 0) + Number(f.monto_crc ?? 0);
      total += Number(f.monto_crc ?? 0);
    }
    return { porTipo, total };
  }, [filtradas]);

  async function guardarConfig() {
    if (!fechaImplInput) { setError('Indique la fecha de implementación.'); return; }
    setSavingConfig(true);
    setError('');
    const { data: userData } = await supabase.auth.getUser();
    const { error: e } = await supabase
      .from('app_ab_produccion_config')
      .upsert({
        empresa_id: empresaId,
        fecha_implementacion: fechaImplInput,
        updated_by: userData.user?.id ?? null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'empresa_id' });
    setSavingConfig(false);
    if (e) { setError(e.message); return; }
    logModuloEvento({
      empresaId, modulo: 'activo_biologico', accion: 'config_fecha_implementacion',
      descripcion: `Fecha de implementación de costeo de producción: ${fechaImplInput}`,
    });
    setShowConfig(false);
    setMensaje('Fecha de implementación guardada.');
    await cargarConfig();
  }

  async function congelar(tipo: 'mo' | 'maq') {
    setProcesando(tipo);
    setError('');
    setMensaje('');
    const rpc = tipo === 'mo' ? 'app_ab_preparar_costos_mo' : 'app_ab_preparar_costos_maquinaria';
    const { data, error: e } = await supabase.rpc(rpc, {
      p_empresa_id: empresaId,
      p_fecha_desde: fechaDesde,
      p_fecha_hasta: fechaHasta,
    });
    setProcesando(null);
    if (e) { setError(e.message); return; }
    logModuloEvento({
      empresaId, modulo: 'activo_biologico', accion: tipo === 'mo' ? 'congelar_costos_mo' : 'congelar_costos_maquinaria',
      descripcion: `${fechaDesde} a ${fechaHasta}`, detalle: data,
    });
    const r = data as any;
    setMensaje(
      tipo === 'mo'
        ? `Mano de obra: ${r?.insertados ?? 0} línea(s) nueva(s). Sin clasificar: ${r?.sin_clasificar ?? 0}. Sin cuenta configurada: ${r?.sin_cuenta ?? 0}.`
        : `Maquinaria: ${r?.insertados ?? 0} línea(s) nueva(s). Sin clasificar: ${r?.sin_clasificar ?? 0}. Sin cuenta configurada: ${r?.sin_cuenta ?? 0}.`,
    );
    await cargar();
  }

  async function crearCif() {
    if (!cifMonto || Number(cifMonto) <= 0) { setError('Indique un monto de CIF mayor a cero.'); return; }
    if (!cifRubro.trim()) { setError('El rubro del CIF es requerido (ej. Vigilancia, Agua, Energía eléctrica finca).'); return; }
    if (!cifDescripcion.trim()) { setError('La descripción del CIF es requerida.'); return; }
    setSavingCif(true);
    setError('');
    const { error: e } = await supabase.from('app_ab_cif_periodos').insert({
      empresa_id: empresaId,
      periodo_desde: cifDesde,
      periodo_hasta: cifHasta,
      monto_total_crc: Number(cifMonto),
      rubro_nombre: cifRubro.trim(),
      descripcion: cifDescripcion.trim(),
    });
    setSavingCif(false);
    if (e) { setError(e.message); return; }
    logModuloEvento({
      empresaId, modulo: 'activo_biologico', accion: 'cif_periodo_creado',
      descripcion: `${cifDesde} a ${cifHasta}: ${cifRubro.trim()} ${fmtCRC(Number(cifMonto))}`,
    });
    setShowCif(false);
    setCifMonto('');
    setCifRubro('');
    setCifDescripcion('');
    setMensaje('Período de CIF capturado. Ya puede distribuirlo por lote.');
    await cargar();
  }

  async function distribuirCif(id: string) {
    setDistribuyendo(id);
    setError('');
    setMensaje('');
    const { data, error: e } = await supabase.rpc('app_ab_distribuir_cif', {
      p_empresa_id: empresaId,
      p_cif_periodo_id: id,
    });
    setDistribuyendo(null);
    if (e) { setError(e.message); return; }
    const r = data as any;
    logModuloEvento({
      empresaId, modulo: 'activo_biologico', accion: 'cif_distribuido',
      descripcion: id, detalle: data,
    });
    setMensaje(`CIF distribuido en ${r?.lineas ?? 0} lote(s) (área total ${Number(r?.area_total_ha ?? 0).toFixed(2)} ha).`);
    await cargar();
  }

  const puedeEditar = permisos.perm_administrar;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className={pageTitleCls}>Costeo de Producción · Activo Biológico</h1>
            <p className={pageSubtitleCls}>
              Insumos + mano de obra + maquinaria + CIF por lote/ciclo (NIC 41).
              {fechaImplementacion ? ` Vigente desde ${fmtDate(fechaImplementacion)}.` : ' Falta configurar la fecha de implementación.'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:flex xl:items-end">
            <div>
              <label className={labelCls}>Desde</label>
              <input type="date" className={inputCls} value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <input type="date" className={inputCls} value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            {puedeEditar && (
              <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={() => setShowConfig(true)}>
                <Settings size={14} />
                Config.
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}
        {mensaje && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-success-bg)', borderColor: 'var(--tone-success-border)', color: 'var(--tone-success-ink)' }}>
            <CheckCircle2 size={17} style={{ color: 'var(--tone-success-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{mensaje}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Kpi label="Total período" value={fmtCRC(totales.total)} icon={<Calculator size={14} />} tone="var(--tone-success-text)" />
          <Kpi label="Insumos" value={fmtCRC(totales.porTipo.insumos)} icon={<BookOpenCheck size={14} />} tone="var(--tone-info-text)" />
          <Kpi label="Mano de obra" value={fmtCRC(totales.porTipo.mano_obra)} icon={<BookOpenCheck size={14} />} tone="var(--emp-accent-txt)" />
          <Kpi label="Maquinaria" value={fmtCRC(totales.porTipo.maquinaria)} icon={<BookOpenCheck size={14} />} tone="var(--tone-warn-text)" />
          <Kpi label="CIF" value={fmtCRC(totales.porTipo.cif)} icon={<BookOpenCheck size={14} />} tone="var(--tone-purple-text)" />
        </div>

        {puedeEditar && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
            <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={() => congelar('mo')} disabled={!!procesando || !fechaImplementacion}>
              {procesando === 'mo' ? <Loader2 size={14} className="animate-spin" /> : <BookOpenCheck size={14} />}
              Congelar mano de obra
            </button>
            <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={() => congelar('maq')} disabled={!!procesando || !fechaImplementacion}>
              {procesando === 'maq' ? <Loader2 size={14} className="animate-spin" /> : <BookOpenCheck size={14} />}
              Congelar maquinaria
            </button>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={() => { setCifDesde(fechaDesde); setCifHasta(fechaHasta); setShowCif(true); }} disabled={!fechaImplementacion}>
              <Plus size={14} />
              Capturar CIF del período
            </button>
            {!fechaImplementacion && (
              <span className="text-xs" style={{ color: 'var(--tone-warn-text)' }}>Configure la fecha de implementación antes de congelar costos.</span>
            )}
          </div>
        )}

        {cifPeriodos.length > 0 && (
          <div className="mb-4 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>Períodos de CIF capturados</div>
            <div className="space-y-1">
              {cifPeriodos.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 text-xs" style={{ borderColor: 'var(--line)' }}>
                  <span>
                    {fmtDate(c.periodo_desde)} – {fmtDate(c.periodo_hasta)} · {c.rubro_nombre ? <strong>{c.rubro_nombre}</strong> : null}{c.rubro_nombre && ' · '}{c.descripcion} · <strong>{fmtCRC(c.monto_total_crc)}</strong>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="uppercase" style={{ color: c.estado === 'distribuido' ? 'var(--tone-success-text)' : 'var(--tone-info-text)' }}>{c.estado}</span>
                    {puedeEditar && c.estado === 'pendiente' && (
                      <button type="button" className={btnSecondary} onClick={() => distribuirCif(c.id)} disabled={distribuyendo === c.id}>
                        {distribuyendo === c.id ? 'Distribuyendo...' : 'Distribuir por lote'}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
            <input className={`${inputCls} pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lote, ciclo o tipo de costo" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando costeo de producción...
          </div>
        ) : filtradas.length === 0 ? (
          <div className="rounded-lg border border-dashed px-5 py-12 text-center" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
            Sin costos de producción en el filtro actual.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="w-full border-collapse text-xs">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}><SortableTh sort={sort} sortKey="periodo">Período</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="lote_num">Lote</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="ciclo_cosecha">Ciclo</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="tipo_costo">Tipo</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="monto_crc">Monto</SortableTh></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((f, i) => (
                  <tr key={`${f.lote_num}-${f.ciclo_cosecha}-${f.tipo_costo}-${f.periodo}-${i}`} className={trCls}>
                    <td className={tdCls}>{fmtDate(f.periodo)}</td>
                    <td className={`${tdCls} font-mono font-bold`} style={{ color: 'var(--emp-accent-txt)' }}>{f.lote_num}</td>
                    <td className={tdCls}>{f.ciclo_cosecha}</td>
                    <td className={tdCls}>{TIPO_LABEL[f.tipo_costo]}</td>
                    <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(f.monto_crc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 rounded-lg border px-4 py-3 text-xs leading-5" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
          Fase 1: esta vista congela evidencia de costo por lote/ciclo. No genera asientos contables todavía.
          Solo boletas de labores/maquinaria en estado <strong>Aprobada</strong> y con fecha igual o posterior a la
          fecha de implementación entran aquí — lo anterior sigue siendo responsabilidad de la base legacy de VFP.
        </div>
      </div>

      {showConfig && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onMouseDown={() => setShowConfig(false)}>
          <div className="w-full max-w-md rounded-xl border p-5" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="mb-3 text-sm font-bold" style={{ color: 'var(--ink)' }}>Fecha de implementación</div>
            <p className="mb-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
              Desde esta fecha en adelante, este motor reemplaza a VFP para costo de mano de obra, maquinaria y CIF.
              No se puede congelar nada anterior a esta fecha.
            </p>
            <label className={labelCls}>Fecha</label>
            <input type="date" className={inputCls} value={fechaImplInput} onChange={(e) => setFechaImplInput(e.target.value)} />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className={btnSecondary} onClick={() => setShowConfig(false)} disabled={savingConfig}>Cancelar</button>
              <button type="button" className={btnPrimary} onClick={guardarConfig} disabled={savingConfig}>
                {savingConfig ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCif && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onMouseDown={() => setShowCif(false)}>
          <div className="w-full max-w-md rounded-xl border p-5" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="mb-3 text-sm font-bold" style={{ color: 'var(--ink)' }}>Capturar CIF del período</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Desde</label>
                <input type="date" className={inputCls} value={cifDesde} onChange={(e) => setCifDesde(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Hasta</label>
                <input type="date" className={inputCls} value={cifHasta} onChange={(e) => setCifHasta(e.target.value)} />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Monto total CRC</label>
              <input className={inputCls} value={cifMonto} onChange={(e) => setCifMonto(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" />
            </div>
            <div className="mt-3">
              <label className={labelCls}>Rubro</label>
              <input className={inputCls} value={cifRubro} onChange={(e) => setCifRubro(e.target.value)} placeholder="Ej. Vigilancia, Agua, Energía eléctrica finca" />
              <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                Determina la cuenta contable de este CIF. Si el rubro no existe todavía en el catálogo, se crea
                automáticamente bajo Costos Indirectos de Producción; si ya existe (mismo nombre), se reutiliza.
              </p>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Descripción</label>
              <input className={inputCls} value={cifDescripcion} onChange={(e) => setCifDescripcion(e.target.value)} placeholder="Ej. Administración de finca, agosto 2026" />
            </div>
            <p className="mt-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
              Se distribuirá entre los lotes con bloques activos en este rango, ponderado por área (ha). Puede
              distribuirlo desde la lista de períodos de CIF después de guardarlo.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className={btnSecondary} onClick={() => setShowCif(false)} disabled={savingCif}>Cancelar</button>
              <button type="button" className={btnPrimary} onClick={crearCif} disabled={savingCif}>
                {savingCif ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CosteoProduccionABPage;
